import React, { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { businessAttributesValidationSchema } from "../../utils/ValidationSchema";
import { BusinessAttributesFormType } from "../../utils/formType";
import { BusinessAttributesInitialFormValues } from "../../utils/initialFormValues";
import { BusinessAttributeType } from "types";
import BusinessAttributesComponent from "./components/BusinessAttributesComponent";
import { useUpdateBusinessAttributesMutation } from "types/generated/operations";
import { getProspectUuid } from "utils/idHelpers";
import { convertStringIntoBool, errorMessageMap } from "utils/helper";
import FooterButton from "uicomponents/FooterButton";
import Alert from "uicomponents/Alert";
import {
  BACK_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
  LOADING_BUTTON_TEXT,
} from "pages/WorkersCompApplcation/utils/contants";
import {
  trackFormError,
  trackPageViewUserStep,
} from "utils/analytics/trackEvent";

interface IBusinessAttributesProps {
  setNextStep: () => void;
  setPreviousStep: () => void;
  data: BusinessAttributeType;
}

const BusinessAttributes = ({
  ...props
}: IBusinessAttributesProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(6, "business attributes");
  }, []);
  const { setNextStep, setPreviousStep, data } = props;

  const initialFormValues: BusinessAttributesFormType =
    BusinessAttributesInitialFormValues(data);

  const [
    updateBusinessAttributes,
    { loading: updateBusinessAttributesLoading },
  ] = useUpdateBusinessAttributesMutation();

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);

  const handleOnSubmit = async (values: BusinessAttributesFormType) => {
    try {
      setIsLoadingSteps(true);
      await updateBusinessAttributes({
        variables: {
          uuid: getProspectUuid(),
          hasTaxLeinBankrupcy: convertStringIntoBool(
            values.hasTaxLeinBankrupcy
          ),
          hasWrittenSafteyProg: convertStringIntoBool(
            values.hasWrittenSafteyProg
          ),
          hazardousMats: convertStringIntoBool(values.hazardousMats),
          offerHealthInsurance: convertStringIntoBool(
            values.offerHealthInsurance
          ),
          ownAircraftWatercraft: convertStringIntoBool(
            values.ownAircraftWatercraft
          ),
          hasVolunteerAccidentPolicy: convertStringIntoBool(
            values.hasVolunteerAccidentPolicy
          ),
          sponsorSportsTeams: convertStringIntoBool(values.sponsorSportsTeams),
          use3rdPartyVendors: convertStringIntoBool(values.use3rdPartyVendors),
          hasVolunteers: convertStringIntoBool(values.hasVolunteers),
          hasHadWorkersCompPolicy: convertStringIntoBool(
            values.hasHadWorkersCompPolicy
          ),
          numberVolunteers: Number(values.numberVolunteers),
          volunteerActivities: values.volunteerActivities,
          volunteerTravel: convertStringIntoBool(values.volunteerTravel),
          beenCancelled: convertStringIntoBool(values.beenCancelled),
          beenDeclined: convertStringIntoBool(values.beenDeclined),
          hasPastDuePremiums: convertStringIntoBool(values.hasPastDuePremiums),
          hasPriorCarriers: convertStringIntoBool(values.hasPriorCarriers),
          policyCancelledReason: values.policyCancelledReason,
          priorCarriers: values.priorCarriers,
          thirdPartyDetails1: values.thirdPartyDetails1,
          thirdPartyDetails2: values.thirdPartyDetails2,
          thirdPartyDetails3: values.thirdPartyDetails3,
          thirdPartyDetails4: values.thirdPartyDetails4,
        },
      });
      setNextStep();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  const handleBack = (): void => {
    setPreviousStep();
  };

  const fieldRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Helper function that makes the browser scroll a specific field into view
  const focusField = (fieldName: string) => {
    if (fieldRefs.current[fieldName]) {
      fieldRefs.current[fieldName]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <>
      <Formik
        initialValues={initialFormValues}
        validationSchema={businessAttributesValidationSchema}
        onSubmit={handleOnSubmit}
      >
        {({ errors, handleSubmit }) => {
          return (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                const firstErrorFieldName = Object.keys(errors)[0];
                focusField(firstErrorFieldName);
                handleSubmit(e);
              }}
            >
              <BusinessAttributesComponent
                data={props.data?.applications?.[0]}
                fieldRefs={fieldRefs}
              />
              <FooterButton
                nextButtonText={
                  updateBusinessAttributesLoading || isLoadingSteps
                    ? LOADING_BUTTON_TEXT
                    : NEXT_BUTTON_TEXT
                }
                backButtonText={BACK_BUTTON_TEXT}
                disabled={updateBusinessAttributesLoading || isLoadingSteps}
                handleBackClick={handleBack}
              />
              {isError && <Alert message={errorMessage} />}
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default BusinessAttributes;
